import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RiskLevel } from "@/lib/api/triggers-knowledge-base";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

export const triggerFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  examples: z.string().min(1, "Examples are required"),
  context: z.string().min(1, "Context is required"),
  instances: z.string().min(1, "Instances are required"),
  suggested_actions: z.string().min(1, "Suggested actions are required"),
  risk_level: z.nativeEnum(RiskLevel),
  category: z.string().min(1, "Category is required"),
});

export type TriggerFormValues = z.infer<typeof triggerFormSchema>;

interface TriggerFormProps {
  defaultValues?: Partial<TriggerFormValues>;
  onSubmit: (values: TriggerFormValues) => void;
  isSubmitting?: boolean;
}

const CATEGORY_OPTIONS = [
  { label: "Foundational", value: "foundational" },
  { label: "Value Identification", value: "value_identification" },
];

export function TriggerForm({
  defaultValues,
  onSubmit,
  isSubmitting = false,
}: TriggerFormProps) {
  const form = useForm<TriggerFormValues>({
    resolver: zodResolver(triggerFormSchema),
    defaultValues: defaultValues || {
      title: "",
      examples: "",
      context: "",
      instances: "",
      suggested_actions: "",
      risk_level: RiskLevel.LOW,
      category: "foundational",
    },
  });

  const { isDirty, dirtyFields } = form.formState;

  const handleSubmit = (values: TriggerFormValues) => {
    if (defaultValues) {
      // Create an object containing only the changed fields
      const changedValues = (
        Object.keys(dirtyFields) as Array<keyof TriggerFormValues>
      ).reduce((acc, key) => {
        if (key === "risk_level") {
          acc[key] = values[key] as RiskLevel;
        } else if (dirtyFields[key]) {
          // We know the value is valid because it passed schema validation
          acc[key] = values[key];
        }
        return acc;
      }, {} as Partial<TriggerFormValues>);

      onSubmit(changedValues as TriggerFormValues);
    } else {
      onSubmit(values);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter trigger title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(({ label, value }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="risk_level"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Risk Level</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select risk level" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(RiskLevel).map((level) => (
                    <SelectItem key={level} value={level}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="examples"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Examples</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter examples of this trigger"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="context"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Context</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter context information"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="instances"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instances</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter instances or examples"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="suggested_actions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Suggested Actions</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter suggested actions"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {isSubmitting
              ? "Saving..."
              : isDirty
                ? "Save Changes"
                : "No Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
